import React, { Fragment, useEffect, useState } from 'react';
import {
  SignificantOtherPersonalInfo as SignificantOtherPersonalInfoKey,
  SignificantOtherContactInfo as SignificantOtherContactInfoKey,
  SignificantOtherEmploymentInfo as SignificantOtherEmploymentInfoKey,
} from 'Consts/Questionnaire';
import { advisorView } from 'Context/ViewContext';
import TextInputGroup from 'Components/Universal/Questionnaire/Fields/TextInputGroup';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import fr from 'facet-rest';
import Section from '../Section';
import TextInput from '../Fields/TextInput';
import TextareaInput from '../Fields/TextareaInput';
import BoolInput from '../Fields/BoolInput';
import DateInput from '../Fields/DateInput';
import RadioInput from '../Fields/RadioInput';
import PhoneInput from '../Fields/PhoneInput';
import SelectInput from '../Fields/SelectInput';
import SocialSecurityInput from '../Fields/SocialSecurityInput';
import USStateInput from '../Fields/USStateInput';

const Employment = props => (
  <Fragment>
    <BoolInput
      {...props}
      name="significantOtherIsEmployed"
      answerKey="significantOtherIsEmployed"
      checkedChildren={
        <Fragment>
          <TextInput
            placeholder="Employer"
            name="significantOtherEmployerName"
            answerKey="significantOtherEmployerName"
            requiredView={advisorView}
          />
          <TextInput
            placeholder="Employer address 1"
            name="significantOtherEmployerAddress1"
            answerKey="significantOtherEmployerAddress1"
          />
          <TextInput
            placeholder="Employer address 2"
            name="significantOtherEmployerAddress2"
            answerKey="significantOtherEmployerAddress2"
          />
          <TextInput
            placeholder="Employer city"
            name="significantOtherEmployerCity"
            answerKey="significantOtherEmployerCity"
          />
          <USStateInput
            label="Employer state"
            name="significantOtherEmployerState"
            answerKey="significantOtherEmployerState"
          />
          <TextInput
            placeholder="Employer zip"
            name="significantOtherEmployerZip"
            answerKey="significantOtherEmployerZip"
          />
          <PhoneInput
            placeholder="Employer office phone"
            name="significantOtherEmployerOfficePhone"
            answerKey="significantOtherEmployerOfficePhone"
          />
          <PhoneInput
            placeholder="Employer cell phone"
            name="significantOtherEmployerCellPhone"
            answerKey="significantOtherEmployerCellPhone"
          />
          <TextInput
            placeholder="Job title"
            name="significantOtherJobTitle"
            answerKey="significantOtherJobTitle"
            requiredView={advisorView}
          />
          <DateInput
            placeholder="Date of Hire"
            name="significantOtherDateOfHire"
            answerKey="significantOtherDateOfHire"
            requiredView={advisorView}
          />
        </Fragment>
      }
    />
  </Fragment>
);

const SignificantOtherPersonalInfo = () => {
  const citizenDetails = 'significantOtherCitizenshipDetails';
  const usaAlpha3 = 'USA';

  const [showResidentType, setShowResidentType] = useState(false);
  const [clientMetaState, setClientMetaState] = useState();


  const { householdResource } = useSimpleHousehold();

  const getClientMeta = async (hhResource) => {
    const clientsCollection = await hhResource.relationship(fr.relationships.clients.name).getRelated();
    setClientMetaState(clientsCollection.meta());
  };

  useEffect(() => {
    if (!householdResource) {
      return;
    }

    getClientMeta(householdResource);
  }, [householdResource]);

  if (!clientMetaState) {
    return null;
  }

  const citizenshipOptions = clientMetaState.field('citizenshipDetails').options().list().map(option => ({
    id: option.id(),
    value: option.attr('prompt'),
  }));
  citizenshipOptions.unshift({ id: '', value: '- Please select -' });

  const residentTypeOptions = clientMetaState.field('residentType').options().list().map(option => ({
    id: option.id(),
    value: option.attr('prompt'),
  }));
  residentTypeOptions.unshift({ id: '', value: '- Please select -' });

  const handleCitizenshipChange = (k, v) => {
    if (k !== citizenDetails) return;
    setShowResidentType(v !== usaAlpha3 && v !== '');
  };

  return (
    <Section
      transitionSpeed={0.25}
      title="Significant Other Personal Information"
      sectionKey={SignificantOtherPersonalInfoKey}
    >
      <TextInputGroup
        inputConfigs={[
          {
            label: 'First Name',
            isRequired: true,
            answerKey: 'significantOtherFirstName',
          },
          {
            label: 'Preferred Name',
            answerKey: 'significantOtherNickname',
          },
          {
            label: 'Middle Name',
            answerKey: 'significantOtherMiddleName',
          },
          {
            label: 'Last Name',
            isRequired: true,
            answerKey: 'significantOtherLastName',
          },
        ]}
      />
      <SocialSecurityInput
        placeholder="Social security number"
        name="significantOtherSSN"
        answerKey="significantOtherSSN"
      />
      <DateInput
        placeholder="Date of birth"
        name="significantOtherDOB"
        answerKey="significantOtherDOB"
        displayInLocalTime={false}
        requiredView={advisorView}
      />
      <DateInput
        placeholder="Anniversary Date"
        name="significantOtherAnniversaryDate"
        answerKey="significantOtherAnniversaryDate"
        displayInLocalTime={false}
        requiredView={advisorView}
      />
      <SelectInput
        label="Gender"
        name="significantOtherGender"
        answerKey="significantOtherGender"
        requiredView={advisorView}
        options={[
          { id: '', value: '- Please select -' },
          { id: 'female', value: 'Female' },
          { id: 'male', value: 'Male' },
          { id: 'other', value: 'Other' },
        ]}
      />
      <SelectInput
        label="Country of Citizenship"
        name="significantOtherCitizenshipDetails"
        answerKey="significantOtherCitizenshipDetails"
        options={citizenshipOptions}
        onChange={handleCitizenshipChange}
      />
      {showResidentType && (
        <SelectInput
          label="Resident Type"
          name="significantOtherResidentType"
          answerKey="significantOtherResidentType"
          options={residentTypeOptions}
        />
      )}
    </Section>
  );
};

SignificantOtherPersonalInfo.propTypes = {
};

const SignificantOtherContactInfo = () => (
  <Section
    transitionSpeed={0.25}
    title="Significant Other Contact Information"
    sectionKey={SignificantOtherContactInfoKey}
  >
    <PhoneInput
      placeholder="Cell phone"
      name="significantOtherCellPhone"
      answerKey="significantOtherCellPhone"
      requiredView={advisorView}
    />
    <PhoneInput
      placeholder="Home phone"
      name="significantOtherHomePhone"
      answerKey="significantOtherHomePhone"
      requiredView={advisorView}
    />
    <PhoneInput
      placeholder="Other phone"
      name="significantOtherOtherPhone"
      answerKey="significantOtherOtherPhone"
      requiredView={advisorView}
    />
    <RadioInput
      label="Preferred contact method"
      name="significantOtherPreferredContactMethod"
      answerKey="significantOtherPreferredContactMethod"
      options={[
        {
          id: 'text',
          text: 'Text',
        },
        {
          id: 'email',
          text: 'Email',
        },
        {
          id: 'phone',
          text: 'Phone',
        },
      ]}
    />
  </Section>
);

const SignificantOtherEmploymentInfo = () => (
  <Section
    transitionSpeed={0.25}
    title="Significant Other Employment Information"
    sectionKey={SignificantOtherEmploymentInfoKey}
  >
    <BoolInput
      label="Is your significant other retired?"
      name="significantOtherIsRetired"
      answerKey="significantOtherIsRetired"
      uncheckedChildren={
        <Employment label="Is your significant other employed?" />
      }
      checkedChildren={
        <Employment label="Does your significant other work in retirement?" />
      }
    />
    <BoolInput
      label="Is your significant other considering a job change?"
      name="significantOtherIsConsideringJobChange"
      answerKey="significantOtherIsConsideringJobChange"
      checkedChildren={
        <TextareaInput
          placeholder="Details"
          name="significantOtherConsideringJobChangeDetails"
          answerKey="significantOtherConsideringJobChangeDetails"
        />
      }
    />
    <BoolInput
      label="Is ok to contact your significant other at work?"
      name="significantOtherOkayToContactAtWork"
      answerKey="significantOtherOkayToContactAtWork"
    />
  </Section>
);

const SignificantOtherAllSections = () => (
  <Fragment>
    <SignificantOtherPersonalInfo />
    <SignificantOtherContactInfo />
    <SignificantOtherEmploymentInfo />
  </Fragment>
);

export {
  SignificantOtherPersonalInfo,
  SignificantOtherContactInfo,
  SignificantOtherEmploymentInfo,
  SignificantOtherAllSections,
};
